import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

import {CLIMATE_RISK_INDEX_CLASSES, getColorForClimateRiskIndex} from "@reva/shared/climate-risk";

import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import MapLayerUnavailableHint from "../../component/MapLayerUnavailableHint";
import MissingPermissionIndicator from "../../component/MissingPermissionIndicator";
import OpenLayersMap from "../../component/OpenLayersMap";
import {useRevaTranslation, useSelectByCurrentLanguage} from "../../hooks/languageHooks";
import {navigateToRouteAndHash} from "../../hooks/navigationHooks";
import {useMapSettings} from "../../hooks/useMapSettings";
import {selectAddressData} from "../../redux/addressSlice";
import {selectClimateRiskData, selectClimateRiskError, selectClimateRiskLoading} from "../../redux/locationInfoSlice";
import {LocationInfoEndpoint, queryLocationInfoAtCoordinateAsync} from "../../redux/locationInfoThunk";
import {selectSegment} from "../../redux/objectParametersSlice";
import {selectLocationInfoClimateRiskSpec, selectSpec} from "../../redux/specSlice";
import {LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND, selectCapabilities} from "../../redux/wmtsSlice";
import {getTranslationForClimateRiskIndex} from "../../util/climateRiskUtil";
import {LayerFactory, resolutionFromScale} from "../../util/maps";
import {tooltipHandler} from "../../util/mapTooltipUtil";
import {PERMISSION_RULE_CLIMATERISK} from "../../util/permissions";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";

const hazards = ["lightning", "earthquake", "heat", "snow", "rain", "hail", "forest fire", "winterstorm", "flood"];
const indices = hazards.map((v) => `index_${v.toLowerCase().replace(" ", "_")}`);
const index_categories = hazards.map((v) => `index_category_${v.toLowerCase().replace(" ", "_")}`);
const trends = hazards.map((v) => `trend_${v.toLowerCase().replace(" ", "_")}`);

function getTranslationForRisk(translation, key) {
    return translation(`climaterisk.${key}`);
}

// Nur den ersten Buchstaben kleinschreiben ("Keine Gefährdung" -> "keine Gefährdung"),
// damit die Klassennamen sich in Fließtext/Tabelle einfügen, ohne Substantive zu verlieren.
function lowercaseFirst(text) {
    return text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
}

const trendMeta = {
    "-2": {icon: "pi-arrow-down", key: "trend_decrease_strong"},
    "-1": {icon: "pi-arrow-down-right", key: "trend_decrease"},
    0: {icon: "pi-arrow-right", key: "trend_equal"},
    1: {icon: "pi-arrow-up-right", key: "trend_increase"},
    2: {icon: "pi-arrow-up", key: "trend_increase_strong"},
};

function TrendIcon({value}) {
    const {t} = useTranslation();
    const meta = trendMeta[value];
    if (!meta) return null;
    return <i style={{fontWeight: "bold"}} className={`pi ${meta.icon}`} title={t(`climaterisk.${meta.key}`)}></i>;
}

export const FLUTRISIKEN_KARTE = "reva:flutrisiken_karte_3857";
export const KLIMARISIKEN_KARTE = "reva:klimarisiken_karte_3857";

function IndexLegende() {
    const selectLang = useSelectByCurrentLanguage();
    const spec = useSelector(selectLocationInfoClimateRiskSpec);
    if (!spec) return null;
    const categories = spec.outputParameters
        .filter((op) => "index_category_total" === op.parameter)
        .flatMap((c) => c.categories);
    const rows = categories.map((c, i) => ({
        label: selectLang(c.shortNameDe, c.shortNameEn),
        value: c.titleDe.toString().replace(/^.*\(([^()]+).*/, "$1"),
        ...CLIMATE_RISK_INDEX_CLASSES?.[i],
    }));
    return (
        <div>
            {rows.map((r) => (
                <div key={r.key} className="flex items-center mb-2">
                    <span
                        className="flex items-center justify-center rounded font-bold mr-4"
                        style={{width: "7em", height: "1.6em", backgroundColor: r.color, color: "rgb(75, 85, 99)"}}
                    >
                        {r.value}
                    </span>
                    <span>{r.label}</span>
                </div>
            ))}
        </div>
    );
}

function ClimateriskMap({climateriskData, selectedHazard}) {
    const addressData = useSelector(selectAddressData);
    const capabilities = useSelector(selectCapabilities);
    const {t, language} = useRevaTranslation();
    const dispatch = useDispatch();
    const {initialView, onMapMoveEnd} = useMapSettings("climaterisk_map_settings");
    /** @type {AggregatedTimelineData|null} */
    const backgroundLayerCapabilities = capabilities?.[LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND];

    const getClimateriskData = (coordinate) =>
        dispatch(queryLocationInfoAtCoordinateAsync(coordinate, LocationInfoEndpoint.CLIMATE_RISK));

    if (!addressData || !climateriskData || !backgroundLayerCapabilities) return null;
    const hazardLayerMeta = hazards.map((v, i) => {
        if (v === "flood") {
            return {
                title: v,
                layer: FLUTRISIKEN_KARTE,
                params: {env: "index:anzeigewert"},
                style: "klimarisiken_style",
                opacity: 0.79,
                visible: selectedHazard === v,
            };
        }
        const index = indices[i];
        const visible = v === selectedHazard;
        // Alle Nicht-Flut-Layer 10% durchsichtiger als Flut (0.79 -> 0.69).
        return {
            title: v,
            layer: KLIMARISIKEN_KARTE,
            params: {env: `index:${index}`},
            style: null,
            opacity: 0.69,
            visible: visible,
        };
    });
    const hazardlayers = hazardLayerMeta.map((h) =>
        LayerFactory.createGeoserverWmsLayer(h.layer, addressData, h.style, h.title, h.opacity, {
            visible: h.visible,
            params: h.params,
            title: getTranslationForRisk(t, h.title),
            type: "base",
        }),
    );
    const mapModel = {
        view: {
            lat: initialView?.lat ?? addressData.lat,
            lon: initialView?.lon ?? addressData.lon,
            // Start auf rundem Maßstab 1:600.000 (doppelt so weit herausgezoomt wie der frühere
            // Default 1:300.000), sofern keine gespeicherte Ansicht existiert
            ...(initialView?.zoom != null
                ? {zoom: initialView.zoom}
                : {resolution: resolutionFromScale(600000, addressData.lat)}),
        },
        layers: [
            LayerFactory.createGeoserverWmtsLayer(
                LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND,
                addressData,
                undefined,
                "background",
                undefined,
                {
                    ...backgroundLayerCapabilities,
                },
            ),
            ...hazardlayers,
            LayerFactory.createMarkerLayer(addressData.address, language),
            LayerFactory.createMarkerLayer([], language, "tooltip-markers"),
        ],
        eventHandlers: {
            click_event: (evt, map) => tooltipHandler(evt, map, getClimateriskData, getTootipText),
            hover_feature_event: (evt, map) => tooltipHandler(evt, map, getClimateriskData, getTootipText),
            moveend_event: onMapMoveEnd,
        },
        scaleLine: {enabled: true},
    };

    function getTootipText(locationData) {
        // Kein Tooltip, wenn keine Daten vorliegen (z.B. Abfrage außerhalb Deutschlands
        // liefert ein Objekt mit lauter NULL-Werten). Nur dann anzeigen, wenn mindestens
        // ein Gefahren-Wert vorhanden ist.
        if (!locationData) return;
        const hasData = hazards.some(
            (_, index) => locationData[indices[index]] != null || locationData[index_categories[index]] != null,
        );
        if (!hasData) return;
        const tooltipData = hazards.map((hazard, index) => {
            const value_localized = locationData[indices[index]];
            const index_category = index_categories[index];
            const index_category_value = locationData[index_category];
            const class_localized = getTranslationForClimateRiskIndex(t, index_category_value);
            const name_localized = getTranslationForRisk(t, hazard);
            return {
                name: name_localized,
                class: class_localized,
                value: value_localized,
            };
        });
        const makeBold = (value) =>
            typeof value !== "number" ? "font-bold text-color" : value > 34 ? "font-semibold" : "";

        return [
            {
                name: t("climaterisk.risk_type"),
                class: t("climaterisk.class_header"),
                value: t("climaterisk.index_header"),
            },
        ]
            .concat(tooltipData)
            .reduce(
                (acc, v, i) =>
                    acc +
                    (i === 1 ? "<br>" : "") +
                    `<div class='flex'>
                        <span class='w-44 ${makeBold(v.value)}'>${v.name}</span>
                        <span class='w-40 ${makeBold(v.value)}'>${v.class}</span>
                        <span class='w-12 ${makeBold(v.value)}'>${v.value}</span>
                    </div>`,
                "",
            );
    }

    return (
        <OpenLayersMap model={mapModel} legend={<IndexLegende />} legendOptions={{visible: true, dismissable: false}} />
    );
}

/**
 * Check if a parameter (also e.g. a "DOC_PROPERTY", not part of the AVM spec) is shown as an input
 * row on the object/overview page for the given segment.
 * @param {RevaSpec} objectSpec full spec (spec.objectInputs)
 * @param {AvmSpecSegment} segment current segment
 * @param {string} parameter parameter to look for
 * @return {boolean}
 */
function isObjectOverviewInputVisible(objectSpec, segment, parameter) {
    const row = objectSpec?.objectInputs
        ?.find((page) => page.key === "object/overview")
        ?.columns.flatMap((c) => c.blocks)
        .flatMap((b) => b.rows)
        .find((r) => r.parameter === parameter);
    if (!row) return false;
    return (
        !Array.isArray(row.onlyVisibleInSegments) ||
        !row.onlyVisibleInSegments.length ||
        row.onlyVisibleInSegments.includes(segment)
    );
}

/**
 *
 * @returns {React.JSX.Element}
 */
export function TabClimaterisk() {
    const {t, language} = useRevaTranslation();
    const navigate = useNavigate();
    const objectSpec = useSelector(selectSpec);
    const segment = useSelector(selectSegment);
    const hasFloodRiskInput = isObjectOverviewInputVisible(objectSpec, segment, "location_flood_risk");
    const spec = useSelector(selectLocationInfoClimateRiskSpec);
    const climateriskData = useSelector(selectClimateRiskData);
    const climateRiskLoading = useSelector(selectClimateRiskLoading);
    const queryError = useSelector(selectClimateRiskError);
    const [selectedHazard, setSelectedHazard] = useState(climateriskData?.index_max_hazard_en?.toLowerCase());

    useEffect(() => {
        setSelectedHazard(climateriskData?.index_max_hazard_en?.toLowerCase());
    }, [climateriskData?.index_max_hazard_en]);

    if (queryError) {
        return <ErrorIndicator i18nKey={"climaterisk.error"} errorObject={queryError} />;
    }
    if (climateRiskLoading || !climateriskData || !spec) {
        return <LoadingIndicator loading={!climateriskData || !spec} />;
    }

    function ClimateRiskSummary() {
        const riskClass = lowercaseFirst(getTranslationForClimateRiskIndex(t, climateriskData.index_category_total));
        const noRisk = Number(climateriskData.index_category_total) === 0;
        const text = noRisk
            ? t("climaterisk.total_risk_sentence_none", {index: climateriskData.index_total})
            : t("climaterisk.total_risk_sentence", {riskClass: riskClass, index: climateriskData.index_total});
        const maxHazardKey = climateriskData.index_max_hazard_en?.toLowerCase();
        const maxHazardText =
            !noRisk && maxHazardKey
                ? t("climaterisk.total_risk_max_hazard", {hazard: getTranslationForRisk(t, maxHazardKey)})
                : null;
        // Einzelrisiken mit höherem Index als das Hauptrisiko (z.B. Blitzschlag): die API
        // gewichtet Hitze und Blitzschlag wegen geringer Gefährdung für Immobilien geringer —
        // das erklären, damit der Satz nicht der Tabelle zu widersprechen scheint.
        const maxHazardValue = climateriskData[indices[hazards.indexOf(maxHazardKey)]];
        const higherHazards =
            maxHazardText && maxHazardValue != null
                ? hazards
                      .map((h, i) => ({hazard: h, index: climateriskData[indices[i]]}))
                      .filter(({hazard, index}) => hazard !== maxHazardKey && index > maxHazardValue)
                      .map(
                          ({hazard, index}) =>
                              `${getTranslationForRisk(t, hazard)} (${t("climaterisk.index_header")} ${index})`,
                      )
                : [];
        const higherText =
            higherHazards.length > 0
                ? t("climaterisk.total_risk_higher_index", {
                      count: higherHazards.length,
                      hazards: new Intl.ListFormat(language, {type: "conjunction"}).format(higherHazards),
                  })
                : null;
        return (
            <p className="m-2 text-lg font-bold">
                {text}
                {maxHazardText && (
                    <>
                        <br />
                        {maxHazardText}
                    </>
                )}
                {higherText && (
                    <>
                        <br />
                        <span className="text-base font-normal">{higherText}</span>
                    </>
                )}
            </p>
        );
    }

    function ClimateRiskTable() {
        const rows = hazards.map((key, i) => {
            const index = climateriskData[indices[i]];
            const index_category = climateriskData[index_categories[i]];
            const trend = climateriskData[trends[i]];
            const color = getColorForClimateRiskIndex(index_category);
            const riskClass = lowercaseFirst(getTranslationForClimateRiskIndex(t, index_category));
            return {key: key, index: index, trend: trend, color: color, category: index_category, riskClass: riskClass};
        });
        const selectedRow = rows.find((r) => r.key === selectedHazard) ?? null;
        // Zeilen-Tooltip: welcher Karten-Layer beim Klick angezeigt wird
        const rowTitle = (r) => t("climaterisk.row_tooltip", {hazard: getTranslationForRisk(t, r.key)});
        return (
            <DataTable
                value={rows}
                dataKey="key"
                size="small"
                rowClassName="text-sm"
                sortField="index"
                sortOrder={-1}
                selectionMode="single"
                selection={selectedRow}
                onSelectionChange={(e) => e.value && setSelectedHazard(e.value.key)}
            >
                <Column
                    header={t("climaterisk.risk_type")}
                    field="key"
                    sortable={true}
                    body={(r) => (
                        <div title={rowTitle(r)} className="flex items-center gap-2">
                            {getTranslationForRisk(t, r.key)}
                            {r.key === "flood" && hasFloodRiskInput && (
                                <i
                                    className="pi pi-pencil ml-2 cursor-pointer"
                                    title={t("climaterisk.flood_risk_link_tooltip")}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigateToRouteAndHash(navigate, "/object/overview", "inputLocationFloodRisk");
                                    }}
                                />
                            )}
                        </div>
                    )}
                />
                <Column
                    header={t("climaterisk.risk_class_header")}
                    field="category"
                    bodyStyle={{width: "25%"}}
                    body={(r) => <div title={rowTitle(r)}>{r.riskClass}</div>}
                />
                <Column
                    header={t("climaterisk.index_header")}
                    field="index"
                    sortable={true}
                    align={"center"}
                    alignHeader={"center"}
                    bodyClassName="p-0"
                    bodyStyle={{width: "20%"}}
                    body={(r) => (
                        <div
                            className="climate-location-table-inner-cell"
                            title={rowTitle(r)}
                            style={{
                                color: "rgb(75, 85, 99)",
                                backgroundColor: r.color,
                                fontWeight: "bold",
                            }}
                        >
                            {r.index}
                        </div>
                    )}
                />
                <Column
                    header={t("climaterisk.trend_header")}
                    field="trend"
                    sortable={true}
                    align={"center"}
                    alignHeader={"center"}
                    bodyStyle={{width: "15%"}}
                    body={(r) => <TrendIcon value={r.trend} />}
                />
            </DataTable>
        );
    }

    function ClimateriskDetails() {
        if (!climateriskData) return null;
        return (
            <div>
                <div className={"element-panel"}>
                    <ClimateRiskSummary />
                </div>
                <div className={"element-panel"}>
                    <ClimateRiskTable />
                </div>
                <div className="element-panel mb-0">
                    <h3 className="mt-0 mb-2">{t("climaterisk.sources_heading")}</h3>
                    <p>{t("climaterisk.sources_intro")}</p>
                    <ul className="mb-0 pl-5 flex flex-col gap-1">
                        <li>{t("climaterisk.sources_bbsr")}</li>
                        <li>{t("climaterisk.sources_bfg")}</li>
                        <li>{t("climaterisk.sources_coastal_climate_office")}</li>
                        <li>{t("climaterisk.sources_bkg")}</li>
                    </ul>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1">
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <AppreciationTabInputText page="location" tab="climaterisk" />
            <MissingPermissionIndicator permissionRule={PERMISSION_RULE_CLIMATERISK}>
                <div className="MapSplit h-full">
                    {/* Linke Spalte in Scroll-Containment kapseln (wie POI-Tab), damit die
                        langen Legenden/Tabellen die Flex-Row NICHT in die Höhe strecken und
                        die Karte so hoch bleibt wie in den anderen Reitern. */}
                    <div className="MapSplitLeft flex flex-col">
                        <div className="flex-1 relative">
                            <div className="absolute w-full h-full overflow-auto">
                                <ClimateriskDetails />
                            </div>
                        </div>
                    </div>
                    <div className="MapSplitRight OpenLayersMapBackdrop relative">
                        <MapLayerUnavailableHint layers={[LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND]} />
                        <ClimateriskMap climateriskData={climateriskData} selectedHazard={selectedHazard} />
                    </div>
                </div>
            </MissingPermissionIndicator>
        </div>
    );
}

export default TabClimaterisk;
